/* eslint-disable @typescript-eslint/explicit-function-return-type */
import IInteractEventTracker, { IInteractEventTrackerAction, IInteractEventTrackerActionAttributes } from './IInteractEventTracker';
import {
  TInteractEventTrackerUuid,
  TInteractEventTrackerTypes,
  TInteractEventTrackerDeviceId,
  TInteractEventTrackerCustomerId,
  TInteractEventTrackerActionAction,
  TInteractEventTrackerActionPlatform,
  TInteractEventTrackerActionAppVersion,
  TInteractEventTrackerActionCurrentTime,
  TInteractEventTrackerActionUserTimeZoneOffset,
  TInteractEventTrackerActionSent
} from '../types/TInteractEventTracker';
import { EInteractEventTrackerActionPlatform } from '../enums/EInteractEventTracker';

import Cookie from '@/helpers/cookie';
import i18n from '@/plugins/i18n';

export default class InteractEventTracker implements IInteractEventTracker {
  uuid?: TInteractEventTrackerUuid = null;
  type?: TInteractEventTrackerTypes = 'event';
  device_id?: TInteractEventTrackerDeviceId = null;
  customer_id: TInteractEventTrackerCustomerId = 'unlogged';
  actions?: IInteractEventTrackerAction[] = new Array<IInteractEventTrackerAction>();

  private get uuidBrowser() {
    return new Cookie({ name: '_gid' }).getSession().value;
  }

  private get deviceIdBrowser() {
    return new Cookie({ name: 'COOKIE_EMP_UUID' }).getSession().value;
  }

  public static get dynamicAttributes() {
    return ['cart_uuid', 'customer_id', 'offer_id', 'offer_category', 'is_order_form_checkout'];
  }

  public get dynamicAttributesValues() {
    const lastActionAttributes = this.actions && this.actions[0].attributes;

    return lastActionAttributes &&
      Object.fromEntries(Object.entries(lastActionAttributes).filter((entrie: [string, any]) => {
        return InteractEventTracker.dynamicAttributes.includes(entrie[0]);
      }));
  }

  constructor(interactEvent?: IInteractEventTracker) {
    Object.assign(this, {
      uuid: this.uuidBrowser || this.deviceIdBrowser,
      device_id: this.deviceIdBrowser,
      ...interactEvent,
    });
  }

  public get actionsToSent() {
    return this.actions && this.actions.filter((action: IInteractEventTrackerAction) => {
      return !action.sent;
    });
  }

  public addAction(action: IInteractEventTrackerAction) {
    this.actions && this.actions.push(action);
  }

  public removeAction(actionName: IInteractEventTrackerAction['action']) {
    this.actions = this.actions && this.actions.map((action: IInteractEventTrackerAction) => {
      if (action.action === actionName) {
        action.sent = true;
      }

      return action;
    });
  }

  public actionAlreadyExist(actionName: IInteractEventTrackerAction['action']) {
    return Boolean(this.actions && this.actions.some((action: IInteractEventTrackerAction) => {
      return action.action === actionName;
    }));
  }

  public actionAlreadySent(actionName: IInteractEventTrackerAction['action']) {
    return Boolean(this.actions && this.actions.some((action: IInteractEventTrackerAction) => {
      if (action.action === actionName) {
        return action.sent;
      }
    }));
  }

  public updateActionAttribute(actionName: IInteractEventTrackerAction['action'], attribute: string, value: any) {
    if (this.actions) {
      this.actions = this.actions.map((action: IInteractEventTrackerAction) => {
        if (actionName === action.action) {
          action.attributes = {
            ...action.attributes,
            [attribute]: value,
          };
        }

        return action;
      }) as any;
    }

    return this.actions;
  }
}

export class InteractEventTrackerAction implements IInteractEventTrackerAction {
  sent?: TInteractEventTrackerActionSent = false;
  action: TInteractEventTrackerActionAction = null;
  attributes?: IInteractEventTrackerActionAttributes = {};
  platform?: TInteractEventTrackerActionPlatform = null;
  app_version?: TInteractEventTrackerActionAppVersion = null;
  current_time?: TInteractEventTrackerActionCurrentTime = null;
  user_timezone_offset?: TInteractEventTrackerActionUserTimeZoneOffset = null;

  private get xpromo() {
    return new URLSearchParams(window.location.search).get('xpromo');
  }

  private get urlQueryParams() {
    return Object.fromEntries(new URLSearchParams(window.location.search));
  }

  private eventDescription(interactEventTrackerAction: IInteractEventTrackerAction) {
    return interactEventTrackerAction !== undefined ? `${i18n.t(`App.Checkout.EventTracker.events.${interactEventTrackerAction.action}`)}` : null;
  }

  constructor(interactEventTrackerAction?: IInteractEventTrackerAction) {
    Object.assign(this, {
      platform: EInteractEventTrackerActionPlatform.web,
      app_version: 'v2',
      current_time: new Date().toLocaleString(),
      user_timezone_offset: new Date().getTimezoneOffset(),
      ...interactEventTrackerAction,
      attributes: {
        xpromo: this.xpromo || 'N/A',
        url_base: window.location.href,
        url_query_string: this.urlQueryParams,
        referrer: document.referrer || 'N/A',
        user_agent: navigator.userAgent,
        event_description: interactEventTrackerAction && this.eventDescription(interactEventTrackerAction),
        ...interactEventTrackerAction && interactEventTrackerAction.attributes,
      },
    });
  }
}
